import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import './LandingPage.css';
import './CreatePostPage.css'; // Issue #112: Import styles for camera button
import CreatePost from './CreatePost';
import { AppHeader, BottomNavigation, CreateOptionsMenu, EditPostPopover, EditMarketplaceObjectPopover, MarketplaceObjectMenu, ConfirmationModal, ImageModal, ReportModal, SuccessModal, BadWordsModal, CommentInput, FloatingPlusButton, ShareModal, TabNavigation, MarketplaceHeartButton, Button } from './shared';
import { useLogout } from '../hooks';
import { useNavigation } from '../contexts/NavigationContext';
import { useGlobalModal } from '../contexts/GlobalModalContext';
import { useLanguage } from '../contexts/LanguageContext';
import { useAds } from '../contexts/AdContext'; // Issue #268: Content view tracking
import { AdBanner, NativeAd } from './ads'; // Issue #311: Native ads in feed

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '';

const LandingPage = ({ initialTab, initialMarketplaceFilter, onMarketplaceFilterChange, autoOpenCreatePost, autoOpenCreateMarketplace, onAutoOpenHandled, openCommentsForPostId, onOpenCommentsHandled, sharedPost, onSharedPostHandled }) => {
  const navigation = useNavigation();
  const { openMarketplaceModal } = useGlobalModal();
  const { handleLogout, isLoggingOut, logoutError, clearLogoutError } = useLogout(navigation.onLogout);
  const { t } = useLanguage();
  const { trackContentView, initFromUserData } = useAds(); // Issue #268: Track content views for ad consent
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [postsLoading, setPostsLoading] = useState(false);
  const [error, setError] = useState(null);
  // Restore active tab from localStorage, fallback to initialTab, then 'new'
  const [activeTab, setActiveTab] = useState(() => {
    const savedTab = localStorage.getItem('landingPageTab');
    return savedTab || initialTab || 'new';
  });
  const [expandedPosts, setExpandedPosts] = useState({});
  const [posts, setPosts] = useState([]);
  const [offset, setOffset] = useState(0);
  // Issue #190: Infinite scroll state
  const [hasMore, setHasMore] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const loadMoreRef = useRef(null);
  const canLoadMore = useRef(false);
  // Issue #119: Track images array and index for gallery modal
  const [selectedImageData, setSelectedImageData] = useState(null);
  const LIMIT = 10; // Issue #190: Load 10 at a time

  // Issue #32, #34: Edit and delete functionality
  const [openMenuPostId, setOpenMenuPostId] = useState(null); // Track which post's menu is open
  const [menuOpenUpward, setMenuOpenUpward] = useState(false); // Issue #189: Track menu direction
  const [editingPost, setEditingPost] = useState(null); // Post being edited
  const [deletingPostId, setDeletingPostId] = useState(null); // Post being deleted (for confirmation)
  const [isDeletingPost, setIsDeletingPost] = useState(false);

  // Issue #62: Marketplace object editing state
  // Issue #440: field/image/submission state now lives in the shared
  // EditMarketplaceObjectPopover component
  const [editingMarketplaceObject, setEditingMarketplaceObject] = useState(null);

  // Issue #263: Mark as sold functionality for Marktplatz tab
  const [confirmSoldObjectId, setConfirmSoldObjectId] = useState(null);
  const [isMarkingSold, setIsMarkingSold] = useState(false);

  // Issue #30: Post reporting state
  const [reportingPostId, setReportingPostId] = useState(null); // Post being reported
  const [reportCategory, setReportCategory] = useState(''); // Selected category
  const [reportReason, setReportReason] = useState(''); // Optional reason
  const [isSubmittingReport, setIsSubmittingReport] = useState(false);
  const [reportedPostIds, setReportedPostIds] = useState([]); // Posts user has reported (to hide them)
  const [showReportSuccess, setShowReportSuccess] = useState(false);
  const [showReportReasonInput, setShowReportReasonInput] = useState(false); // Toggle for optional reason textarea

  // Issue #35: Hamburger menu state - logout now handled by useLogout hook

  // Issue #52: Comments functionality
  const [openCommentsPostId, setOpenCommentsPostId] = useState(null); // Track which post's comments are open
  const [comments, setComments] = useState({}); // Comments by post ID: { postId: [comments] }
  const [commentText, setCommentText] = useState({}); // Comment input text by post ID: { postId: text }
  const [isSubmittingComment, setIsSubmittingComment] = useState(false);
  // Issue #161: State for bad words modal (comments)
  const [showCommentBadWordsModal, setShowCommentBadWordsModal] = useState(false);
  const [commentBadWords, setCommentBadWords] = useState([]);
  const [loadingComments, setLoadingComments] = useState(false);

  // Issue #312: Share modal state
  const [shareModalOpen, setShareModalOpen] = useState(false);
  const [shareData, setShareData] = useState({ type: '', id: '', content: '', title: '' });
  // Issue #317: Track highlighted shared post
  const [highlightedPostId, setHighlightedPostId] = useState(null);
  const sharedPostRef = useRef(null);
  // Issue #67: Anonymous comment state
  const [commentIsAnonymous, setCommentIsAnonymous] = useState({}); // Anonymous toggle state by post ID: { postId: boolean }

  // Issue #54: Comment reporting state
  const [openCommentMenuId, setOpenCommentMenuId] = useState(null); // Track which comment's menu is open
  const [reportingCommentId, setReportingCommentId] = useState(null); // Comment being reported
  const [reportCommentCategory, setReportCommentCategory] = useState(''); // Selected category
  const [reportCommentReason, setReportCommentReason] = useState(''); // Optional reason
  const [isSubmittingCommentReport, setIsSubmittingCommentReport] = useState(false);
  const [reportedCommentIds, setReportedCommentIds] = useState([]); // Comments user has reported (to hide them)
  const [showCommentReportSuccess, setShowCommentReportSuccess] = useState(false);
  const [showCommentReportReasonInput, setShowCommentReportReasonInput] = useState(false); // Toggle for optional reason textarea

  // Issue #57: Comment edit and delete state
  const [editingCommentId, setEditingCommentId] = useState(null); // Comment being edited
  const [editCommentText, setEditCommentText] = useState(''); // Text for comment being edited
  const [deletingCommentId, setDeletingCommentId] = useState(null); // Comment being deleted (for confirmation)
  const [isSubmittingCommentEdit, setIsSubmittingCommentEdit] = useState(false);
  const [isDeletingComment, setIsDeletingComment] = useState(false);

  // Issue #56: Create button options menu
  const [showCreateOptions, setShowCreateOptions] = useState(false); // Show/hide create options menu

  // Issue #58: Marketplace main page state
  // Issue #216: Persist marketplace filter in localStorage so "search" objects remain visible after page refresh
  // Priority: localStorage (most recent) > prop > default
  const [marketplaceFilter, setMarketplaceFilter] = useState(() => {
    const savedFilter = localStorage.getItem('marketplaceFilter');
    return savedFilter || initialMarketplaceFilter || 'offer';
  }); // 'offer' or 'search' - default is 'offer' (ich biete)
  // Issue #228: Search triggers only on icon click or Enter key
  const [marketplaceSearchQuery, setMarketplaceSearchQuery] = useState('');
  const [activeMarketplaceSearch, setActiveMarketplaceSearch] = useState('');
  // Issue #288: Search for new posts - triggers only on icon click or Enter key
  const [postsSearchQuery, setPostsSearchQuery] = useState('');
  const [activePostsSearch, setActivePostsSearch] = useState('');
  // Issue #61: Marketplace favorites filter
  const [marketplaceFavoritesOnly, setMarketplaceFavoritesOnly] = useState(false); // Toggle for "Nur Favoriten" filter

  // Post image gallery state - track current image index for each post
  const [postImageIndices, setPostImageIndices] = useState({}); // { postId: currentIndex }

  // Save active tab to localStorage whenever it changes (to persist across page refreshes)
  useEffect(() => {
    localStorage.setItem('landingPageTab', activeTab);
  }, [activeTab]);

  // Issue #216: Save marketplace filter to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('marketplaceFilter', marketplaceFilter);
  }, [marketplaceFilter]);

  // Issue #84: Handle auto-open flags from navigation (e.g., from Profile page Posten button)
  useEffect(() => {
    if (autoOpenCreatePost) {
      // Trigger CreatePost component to open
      setTimeout(() => {
        document.querySelector('.create-post-trigger')?.click();
      }, 100); // Small delay to ensure component is mounted
      onAutoOpenHandled?.();
    } else if (autoOpenCreateMarketplace) {
      // Open marketplace modal using global context
      // Issue #216: Pass onSuccess callback to update filter to match created object type
      openMarketplaceModal({
        type: marketplaceFilter,
        onSuccess: (createdType) => setMarketplaceFilter(createdType)
      });
      onAutoOpenHandled?.();
    }
  }, [autoOpenCreatePost, autoOpenCreateMarketplace, marketplaceFilter, onAutoOpenHandled, openMarketplaceModal]);

  // Issue #87: Handle opening comments for a specific post from notification
  useEffect(() => {
    if (openCommentsForPostId) {
      // Switch to 'new' tab to show posts
      setActiveTab('new');
      // Open comments for the specified post
      setOpenCommentsPostId(openCommentsForPostId);
      // Fetch comments for this post
      const fetchCommentsForPost = async () => {
        setLoadingComments(true);
        try {
          const response = await axios.get(`/api/posts/${openCommentsForPostId}/comments`);
          setComments(prev => ({ ...prev, [openCommentsForPostId]: response.data }));
          // Issue #413: hydrate reported state from backend so refreshes keep comments marked
          const reportedIds = response.data
            .filter(c => c.is_reported_by_user)
            .map(c => c.id);
          if (reportedIds.length > 0) {
            setReportedCommentIds(prev => Array.from(new Set([...prev, ...reportedIds])));
          }
        } catch (err) {
          console.error('Failed to fetch comments:', err);
        } finally {
          setLoadingComments(false);
        }
      };
      fetchCommentsForPost();
      // Clear the flag
      onOpenCommentsHandled?.();
    }
  }, [openCommentsForPostId, onOpenCommentsHandled]);

  // Issue #317: Handle shared post - display at top of feed with highlighting
  useEffect(() => {
    if (sharedPost && sharedPost.id) {
      // Switch to 'new' tab to show posts
      setActiveTab('new');

      // Set highlighted post ID for visual indication
      setHighlightedPostId(sharedPost.id);

      // Prepend the shared post to the posts list if not already present
      setPosts(prevPosts => {
        const exists = prevPosts.some(p => p.id === sharedPost.id);
        if (exists) {
          // Move existing post to top
          const filtered = prevPosts.filter(p => p.id !== sharedPost.id);
          return [sharedPost, ...filtered];
        }
        return [sharedPost, ...prevPosts];
      });

      // Scroll to top to show the shared post
      window.scrollTo({ top: 0, behavior: 'smooth' });

      // Clear the shared post flag after handling
      onSharedPostHandled?.();

      // Remove highlight after 3 seconds
      const timer = setTimeout(() => {
        setHighlightedPostId(null);
      }, 3000);

      return () => clearTimeout(timer);
    }
  }, [sharedPost, onSharedPostHandled]);

  // Handle image modal with URL hash for back button support
  useEffect(() => {
    if (selectedImageData) {
      // Modal opened - set URL hash
      window.location.hash = 'image-modal';
    }
  }, [selectedImageData]);

  // Listen for hash changes to close modal when back button is pressed
  useEffect(() => {
    const handleHashChange = () => {
      // If modal is open and hash is no longer #image-modal, close the modal
      if (selectedImageData && window.location.hash !== '#image-modal') {
        setSelectedImageData(null);
      }
    };

    window.addEventListener('hashchange', handleHashChange);

    return () => {
      window.removeEventListener('hashchange', handleHashChange);
    };
  }, [selectedImageData]);

  useEffect(() => {
    const fetchUserData = async () => {
      try {
        const response = await axios.get('/api/user/me');
        setUserData(response.data);
        // Issue #272: Initialize AdContext with user's consent preferences
        initFromUserData(response.data);
        setLoading(false);
      } catch (err) {
        console.error('Failed to fetch user data:', err);
        if (err.response && err.response.status === 401) {
          setError('Sitzung abgelaufen. Bitte erneut anmelden.');
        } else {
          setError('Fehler beim Laden der Benutzerdaten');
        }
        setLoading(false);
      }
    };

    fetchUserData();
  }, []);

  // Issue #190: Fetch posts with pagination support
  const fetchPosts = useCallback(async (reset = true) => {
    if (!userData) return;

    try {
      if (reset) {
        setPostsLoading(true);
        canLoadMore.current = false;
      } else {
        setIsLoadingMore(true);
      }

      // Issue #48: Fix endpoint selection to properly route marketplace tab
      let endpoint;
      let params = {
        limit: LIMIT,
        offset: reset ? 0 : offset
      };

      if (activeTab === 'hot') {
        endpoint = '/api/posts/hot';
      } else if (activeTab === 'market') {
        endpoint = '/api/marketplace';
        // Issue #58: Add marketplace filter and search parameters
        if (marketplaceFilter) {
          params.type = marketplaceFilter;
        }
        // Issue #228: Use activeMarketplaceSearch (triggered by click/Enter, not keystroke)
        if (activeMarketplaceSearch && activeMarketplaceSearch.trim()) {
          params.search = activeMarketplaceSearch.trim();
        }
        // Issue #61: Add favorites_only parameter
        if (marketplaceFavoritesOnly) {
          params.favorites_only = 'true';
        }
      } else {
        endpoint = '/api/posts/new';
        // Issue #288: Add search parameter for new posts
        if (activePostsSearch && activePostsSearch.trim()) {
          params.search = activePostsSearch.trim();
        }
      }

      const response = await axios.get(endpoint, { params });

      // Issue #190: Append or replace
      if (reset) {
        setPosts(response.data);
      } else {
        setPosts(prev => [...prev, ...response.data]);
      }

      // Issue #268: Track content views for ad consent popover
      // Each loaded post/marketplace object counts as a content view
      response.data.forEach(() => trackContentView());

      // Issue #190: Check if there are more to load
      if (response.data.length < LIMIT) {
        setHasMore(false);
      }

      setPostsLoading(false);
      setIsLoadingMore(false);

      // Issue #190: Enable load-more after delay to prevent immediate trigger
      if (!canLoadMore.current) {
        setTimeout(() => {
          canLoadMore.current = true;
        }, 500);
      }
    } catch (err) {
      console.error('Failed to fetch posts:', err);
      setPosts([]);
      setPostsLoading(false);
      setIsLoadingMore(false);
    }
  }, [userData, offset, activeTab, marketplaceFilter, activeMarketplaceSearch, marketplaceFavoritesOnly, activePostsSearch, trackContentView]);

  // Issue #190: Load more posts callback
  const loadMorePosts = useCallback(() => {
    if (!isLoadingMore && hasMore && !postsLoading && canLoadMore.current) {
      setOffset(prev => prev + LIMIT);
    }
  }, [isLoadingMore, hasMore, postsLoading]);

  // Initial fetch and refetch when tab/filters change
  useEffect(() => {
    setOffset(0);
    setHasMore(true);
    fetchPosts(true);
  }, [userData, activeTab, marketplaceFilter, activeMarketplaceSearch, marketplaceFavoritesOnly, activePostsSearch]);

  // Issue #190: Fetch more when offset changes (not initial)
  useEffect(() => {
    if (offset > 0) {
      fetchPosts(false);
    }
  }, [offset]);

  // Issue #190: IntersectionObserver for infinite scroll
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !postsLoading && !isLoadingMore) {
          loadMorePosts();
        }
      },
      { threshold: 0.1 }
    );

    if (loadMoreRef.current) {
      observer.observe(loadMoreRef.current);
    }

    return () => observer.disconnect();
  }, [hasMore, postsLoading, isLoadingMore, loadMorePosts]);

  const togglePostExpansion = (postId) => {
    setExpandedPosts(prev => ({
      ...prev,
      [postId]: !prev[postId]
    }));
  };

  const handlePostCreated = (newPost) => {
    // Add the new post to the beginning of the posts list
    // This will make it appear at the top of the "New" posts tab
    if (activeTab === 'new') {
      setPosts(prev => [newPost, ...prev]);
    }
  };

  // Issue #189: Toggle menu with position-aware direction
  const handleMenuToggle = (postId, event) => {
    if (openMenuPostId === postId) {
      setOpenMenuPostId(null);
      return;
    }
    const buttonRect = event.currentTarget.getBoundingClientRect();
    const spaceBelow = window.innerHeight - buttonRect.bottom;
    setMenuOpenUpward(spaceBelow < 200);
    setOpenMenuPostId(postId);
  };

  // Issue #32: Edit post handler
  const handleEditPost = (post) => {
    setEditingPost(post);
    setOpenMenuPostId(null);
  };

  // Handle save from EditPostPopover
  const handleSaveEdit = (updatedPost) => {
    setPosts(prevPosts =>
      prevPosts.map(p => p.id === updatedPost.id ? updatedPost : p)
    );
    setEditingPost(null);
  };

  const handleCancelEdit = () => {
    setEditingPost(null);
  };

  // Issue #34: Delete post handler
  const handleDeletePost = (postId) => {
    setDeletingPostId(postId);
    setOpenMenuPostId(null);
  };

  const handleCancelDelete = () => {
    setDeletingPostId(null);
  };

  const handleConfirmDelete = async () => {
    setIsDeletingPost(true);
    try {
      await axios.delete(`/api/posts/${deletingPostId}`);

      // Remove the post from the posts list
      setPosts(prevPosts => prevPosts.filter(p => p.id !== deletingPostId));

      // Close delete modal
      setDeletingPostId(null);
    } catch (err) {
      console.error('Failed to delete post:', err);
      alert(err.response?.data?.message || 'Fehler beim Löschen des Posts');
    } finally {
      setIsDeletingPost(false);
    }
  };

  // Issue #62: Marketplace object edit/delete handlers
  const handleEditMarketplaceObject = (marketplaceObj) => {
    setEditingMarketplaceObject(marketplaceObj);
    setOpenMenuPostId(null);
  };

  // Issue #440: Save handler for shared EditMarketplaceObjectPopover
  const handleSaveMarketplaceEdit = (updatedObject) => {
    setPosts(prevPosts =>
      prevPosts.map(p => p.id === updatedObject.id ? updatedObject : p)
    );
    setEditingMarketplaceObject(null);
  };

  const handleDeleteMarketplaceObject = (postId) => {
    setDeletingPostId(postId);
    setOpenMenuPostId(null);
  };

  // Issue #263: Handle mark as sold for Marktplatz tab
  const handleMarkAsSold = (objectId) => {
    setConfirmSoldObjectId(objectId);
    setOpenMenuPostId(null);
  };

  const handleCancelMarkSold = () => {
    setConfirmSoldObjectId(null);
  };

  const handleConfirmMarkSold = async () => {
    setIsMarkingSold(true);
    try {
      await axios.post(`${API_BASE_URL}/api/marketplace/${confirmSoldObjectId}/mark-sold`);

      // Update the object in the posts list
      setPosts(prevPosts =>
        prevPosts.map(post =>
          post.id === confirmSoldObjectId
            ? { ...post, sold_at: new Date().toISOString() }
            : post
        )
      );

      // Close confirmation modal
      setConfirmSoldObjectId(null);
    } catch (err) {
      console.error('Failed to mark object as sold:', err);
      alert(err.response?.data?.message || 'Fehler beim Markieren als verkauft');
    } finally {
      setIsMarkingSold(false);
    }
  };

  // Issue #30: Report post handlers
  const handleReportPost = (postId) => {
    setReportingPostId(postId);
    setReportCategory('');
    setReportReason('');
    setOpenMenuPostId(null);
  };

  // Issue #312: Share post handler
  const handleSharePost = (post) => {
    // Generate share URL and message based on content type
    const baseUrl = window.location.origin;
    const isMarketplace = activeTab === 'market';

    if (isMarketplace) {
      // Marketplace object sharing
      const shareUrl = `${baseUrl}/share/object/${post.id}`;
      const title = post.title || '';
      const shareText = t('share.objectMessage').replace('{title}', title);
      setShareData({
        type: 'object',
        id: post.id,
        content: title,
        title: title
      });
      setShareModalOpen(true);
    } else {
      // Post sharing
      const shareUrl = `${baseUrl}/share/post/${post.id}`;
      // Get first 30 characters of post content
      const contentPreview = post.content && post.content.length > 30
        ? post.content.substring(0, 30) + '...'
        : post.content || '';
      const shareText = t('share.postMessage').replace('{content}', contentPreview);
      setShareData({
        type: 'post',
        id: post.id,
        content: contentPreview,
        title: 'UNIVERSE Post'
      });
      setShareModalOpen(true);
    }
    setOpenMenuPostId(null);
  };

  const handleCancelReport = () => {
    setReportingPostId(null);
    setReportCategory('');
    setReportReason('');
    setShowReportReasonInput(false);
  };

  const handleSubmitReport = async (category, reason) => {
    setIsSubmittingReport(true);
    try {
      // Marketplace objects are stored in the posts table, so the same
      // /api/posts/<id>/report endpoint handles both posts and marketplace objects.
      const response = await axios.post(`/api/posts/${reportingPostId}/report`, {
        category: category,
        reason: reason
      });

      // Add post to reported posts list to hide it
      setReportedPostIds(prev => [...prev, reportingPostId]);

      // Close report modal
      setReportingPostId(null);

      // Show success message for 3 seconds
      setShowReportSuccess(true);
      setTimeout(() => {
        setShowReportSuccess(false);
      }, 3000);

      // Note: When post/object is auto-deleted (5th report), we keep it in the list
      // with the "gemeldet" badge so the user sees consistent feedback regardless
      // of whether their report triggered auto-deletion or not.
    } catch (err) {
      console.error('Failed to report:', err);
      alert(err.response?.data?.message || 'Fehler beim Melden');
    } finally {
      setIsSubmittingReport(false);
    }
  };

  // Issue #53: Handle like/unlike post
  const handleLikePost = async (postId) => {
    try {
      const response = await axios.post(`/api/posts/${postId}/like`);

      // Update the post's like count and user_has_liked status in the posts list
      setPosts(prevPosts => prevPosts.map(p =>
        p.id === postId
          ? {
              ...p,
              like_number: response.data.like_count,
              user_has_liked: response.data.user_has_liked
            }
          : p
      ));
    } catch (err) {
      console.error('Failed to like/unlike post:', err);
      alert(err.response?.data?.message || 'Fehler beim Liken des Posts');
    }
  };

  // Post image gallery navigation handlers
  // Issue #163: Wrap around - go to first image when at last
  const handleNextPostImage = (postId, totalImages) => {
    setPostImageIndices(prev => {
      const currentIndex = prev[postId] || 0;
      const nextIndex = (currentIndex + 1) % totalImages;
      return { ...prev, [postId]: nextIndex };
    });
  };

  // Issue #163: Wrap around - go to last image when at first
  const handlePrevPostImage = (postId, totalImages) => {
    setPostImageIndices(prev => {
      const currentIndex = prev[postId] || 0;
      const prevIndex = currentIndex === 0 ? totalImages - 1 : currentIndex - 1;
      return { ...prev, [postId]: prevIndex };
    });
  };

  // Issue #52: Toggle comments section and fetch comments
  const handleToggleComments = async (postId) => {
    if (openCommentsPostId === postId) {
      // Close comments
      setOpenCommentsPostId(null);
    } else {
      // Open comments and fetch them if not already loaded
      setOpenCommentsPostId(postId);

      if (!comments[postId]) {
        setLoadingComments(true);
        try {
          const response = await axios.get(`/api/posts/${postId}/comments`);
          setComments(prev => ({ ...prev, [postId]: response.data }));
          // Issue #413: hydrate reported state from backend so refreshes keep comments marked
          const reportedIds = response.data
            .filter(c => c.is_reported_by_user)
            .map(c => c.id);
          if (reportedIds.length > 0) {
            setReportedCommentIds(prev => Array.from(new Set([...prev, ...reportedIds])));
          }
        } catch (err) {
          console.error('Failed to fetch comments:', err);
          alert(err.response?.data?.message || 'Fehler beim Laden der Kommentare');
        } finally {
          setLoadingComments(false);
        }
      }
    }
  };

  // Issue #192: Scroll to comments section when opened
  useEffect(() => {
    if (openCommentsPostId) {
      const timer = setTimeout(() => {
        const el = document.getElementById(`comments-${openCommentsPostId}`);
        if (el) {
          el.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        }
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [openCommentsPostId]);

  // Issue #52: Submit a comment
  const handleSubmitComment = async (postId) => {
    const text = commentText[postId]?.trim();
    if (!text) {
      alert('Bitte gib einen Kommentar ein');
      return;
    }

    setIsSubmittingComment(true);
    try {
      // Issue #67: Include is_anonymous field
      const response = await axios.post(`/api/posts/${postId}/comments`, {
        comment_text: text,
        is_anonymous: commentIsAnonymous[postId] || false
      });

      // Add new comment to the list
      setComments(prev => ({
        ...prev,
        [postId]: [response.data.comment, ...(prev[postId] || [])]
      }));

      // Clear input and reset anonymous toggle
      setCommentText(prev => ({ ...prev, [postId]: '' }));
      setCommentIsAnonymous(prev => ({ ...prev, [postId]: false }));

      // Update comment count in posts
      setPosts(prevPosts => prevPosts.map(p =>
        p.id === postId
          ? { ...p, comment_number: p.comment_number + 1 }
          : p
      ));
    } catch (err) {
      console.error('Failed to submit comment:', err);
      // Issue #161: Check for bad words in response
      if (err.response?.data?.bad_words && err.response.data.bad_words.length > 0) {
        setCommentBadWords(err.response.data.bad_words);
        setShowCommentBadWordsModal(true);
      } else {
        alert(err.response?.data?.message || 'Fehler beim Senden des Kommentars');
      }
    } finally {
      setIsSubmittingComment(false);
    }
  };

  // Issue #54: Comment reporting handlers
  const handleReportComment = (commentId) => {
    setReportingCommentId(commentId);
    setReportCommentCategory('');
    setReportCommentReason('');
    setOpenCommentMenuId(null);
  };

  const handleCancelCommentReport = () => {
    setReportingCommentId(null);
    setReportCommentCategory('');
    setReportCommentReason('');
    setShowCommentReportReasonInput(false);
  };

  const handleSubmitCommentReport = async (category, reason) => {
    setIsSubmittingCommentReport(true);
    try {
      await axios.post(`/api/comments/${reportingCommentId}/report`, {
        category: category,
        reason: reason
      });

      // Add comment to reported comments list to hide it
      setReportedCommentIds(prev => [...prev, reportingCommentId]);

      // Close report modal
      setReportingCommentId(null);

      // Show success message for 3 seconds
      setShowCommentReportSuccess(true);
      setTimeout(() => {
        setShowCommentReportSuccess(false);
      }, 3000);
    } catch (err) {
      console.error('Failed to report comment:', err);
      alert(err.response?.data?.message || 'Fehler beim Melden des Kommentars');
    } finally {
      setIsSubmittingCommentReport(false);
    }
  };

  // Issue #57: Comment edit and delete handlers
  const handleEditComment = (comment) => {
    setEditingCommentId(comment.id);
    setEditCommentText(comment.comment_text);
    setOpenCommentMenuId(null);
  };

  const handleCancelEditComment = () => {
    setEditingCommentId(null);
    setEditCommentText('');
  };

  const handleSaveEditComment = async (commentId, postId) => {
    const text = editCommentText.trim();
    if (!text) {
      alert('Kommentar darf nicht leer sein');
      return;
    }

    setIsSubmittingCommentEdit(true);
    try {
      const response = await axios.put(`/api/comments/${commentId}`, {
        comment_text: text
      });

      // Update comment in the comments state
      setComments(prev => ({
        ...prev,
        [postId]: prev[postId].map(c =>
          c.id === commentId ? response.data.comment : c
        )
      }));

      // Exit edit mode
      setEditingCommentId(null);
      setEditCommentText('');
    } catch (err) {
      console.error('Failed to edit comment:', err);
      // Issue #161: Check for bad words in response
      const badWords = err.response?.data?.bad_words;
      if (badWords && badWords.length > 0) {
        setCommentBadWords(badWords);
        setShowCommentBadWordsModal(true);
      } else {
        alert(err.response?.data?.message || 'Fehler beim Bearbeiten des Kommentars');
      }
    } finally {
      setIsSubmittingCommentEdit(false);
    }
  };

  const handleDeleteComment = (commentId, postId) => {
    setDeletingCommentId({ commentId, postId });
    setOpenCommentMenuId(null);
  };

  const handleCancelDeleteComment = () => {
    setDeletingCommentId(null);
  };

  const handleConfirmDeleteComment = async () => {
    if (!deletingCommentId) return;

    const { commentId, postId } = deletingCommentId;
    setIsDeletingComment(true);
    try {
      await axios.delete(`/api/comments/${commentId}`, {
        headers: { 'Content-Type': 'application/json' }
      });

      // Remove comment from comments state
      setComments(prev => ({
        ...prev,
        [postId]: prev[postId].filter(c => c.id !== commentId)
      }));

      // Update comment count on post
      setPosts(prevPosts => prevPosts.map(p =>
        p.id === postId
          ? { ...p, comment_number: Math.max(0, p.comment_number - 1) }
          : p
      ));

      // Close delete confirmation modal
      setDeletingCommentId(null);
    } catch (err) {
      console.error('Failed to delete comment:', err);
      alert(err.response?.data?.message || 'Fehler beim Löschen des Kommentars');
    } finally {
      setIsDeletingComment(false);
    }
  };

  // Issue #35: Menu and logout now handled by shared components and useLogout hook

  const formatTimeAgo = (isoTimestamp) => {
    const now = new Date();
    const postTime = new Date(isoTimestamp);
    const diffMs = now - postTime;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'vor wenigen Sekunden';
    if (diffMins < 60) return `vor ${diffMins}m`;
    if (diffHours < 24) return `vor ${diffHours}h`;
    if (diffDays < 7) return `vor ${diffDays}d`;

    return postTime.toLocaleDateString('de-DE');
  };

  // Issue #335: Render text with clickable masked links (accessible)
  // Matches links in the format {APP_BASE_URL}/link/{hash} as set by backend
  const renderTextWithLinks = (text) => {
    const linkRegex = /(https?:\/\/[^\s]+\/link\/[a-f0-9]+)/g;
    const parts = text.split(linkRegex);
    return parts.map((part, index) => {
      if (linkRegex.test(part)) {
        const hash = part.split('/link/')[1];
        const apiUrl = `${process.env.REACT_APP_API_URL || ''}/api/link/${hash}`;
        return (
          <a
            key={index}
            href={apiUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="post-link"
            aria-label={t('posts.externalLink') || 'Externer Link (öffnet in neuem Tab)'}
          >
            {part}
            <span className="sr-only"> ({t('posts.opensInNewTab') || 'öffnet in neuem Tab'})</span>
          </a>
        );
      }
      return part;
    });
  };

  const renderPostContent = (post) => {
    const content = post.post_content || post.content;
    const isLong = content.length > 100;
    const isExpanded = expandedPosts[post.id];

    if (!isLong || isExpanded) {
      return (
        <div className="post-content-wrapper">
          <p className="post-content">{renderTextWithLinks(content)}</p>
          {isLong && isExpanded && (
            <button
              className="read-more-button"
              onClick={() => togglePostExpansion(post.id)}
            >
              {t('readLess')}
              <span className="arrow-up">▲</span>
            </button>
          )}
        </div>
      );
    }

    return (
      <div className="post-content-wrapper">
        <p className="post-content">{renderTextWithLinks(content.substring(0, 50))}...</p>
        <button
          className="read-more-button"
          onClick={() => togglePostExpansion(post.id)}
        >
          {t('readMore')}
          <span className="arrow-down">▼</span>
        </button>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="feed-container">
        <p className="loading-text">{t('loading')}</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="feed-container">
        <p className="error-text">{error}</p>
        <div className="error-action">
          <Button variant="primary" onClick={navigation.onLogout}>
            {t('backToLogin')}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <>
    <div className="feed-container">
      {/* Header Navigation Bar - Using shared AppHeader component */}
      <AppHeader
        userData={userData}
        onLogout={handleLogout}
        isLoggingOut={isLoggingOut}
        logoutError={logoutError}
        onClearLogoutError={clearLogoutError}
        onNavigateToLanding={() => setActiveTab('new')}
      >
        {/* Create Post Component */}
        {userData && (
          <CreatePost
            userName={userData.first_name}
            onPostCreated={handlePostCreated}
          />
        )}

        {/* Tab Navigation */}
        <TabNavigation
          activeKey={activeTab}
          tabs={[
            { key: 'hot', label: t('tabs.hotPosts'), onClick: () => setActiveTab('hot') },
            { key: 'new', label: t('tabs.newPosts'), onClick: () => setActiveTab('new') },
            { key: 'market', label: t('tabs.marketplace'), onClick: () => setActiveTab('market') },
          ]}
        />
      </AppHeader>

      {/* Issue #58: Marketplace filters (segmented toggle and search bar) */}
      {activeTab === 'market' && (
        <div style={{
          background: 'var(--bg-primary)',
          padding: '15px 0 5px',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          opacity: 0.8
        }}>
          {/* Segmented Toggle for "ich biete" / "ich suche" */}
          <div style={{ display: 'flex', marginBottom: '10px' }}>
            <button
              onClick={() => {
                setMarketplaceFilter('offer');
                onMarketplaceFilterChange?.('offer');
              }}
              style={{
                fontFamily: 'Poppins, sans-serif',
                fontSize: '10px',
                fontWeight: '500',
                padding: '4px 12px',
                background: marketplaceFilter === 'offer' ? 'var(--bg-nav)' : 'var(--bg-primary)',
                border: '1px solid var(--text-primary)',
                borderRadius: '100px 0 0 100px',
                cursor: 'pointer',
                color: 'var(--text-primary)',
                minWidth: '108px',
                height: '10px',
                transition: 'background 0.2s'
              }}
            >
              {t('tabs.iOffer')}
            </button>
            <button
              onClick={() => {
                setMarketplaceFilter('search');
                onMarketplaceFilterChange?.('search');
              }}
              style={{
                fontFamily: 'Poppins, sans-serif',
                fontSize: '10px',
                fontWeight: '500',
                padding: '4px 12px',
                background: marketplaceFilter === 'search' ? 'var(--bg-nav)' : 'var(--bg-primary)',
                border: '1px solid var(--text-primary)',
                borderLeft: 'none',
                borderRadius: '0 100px 100px 0',
                cursor: 'pointer',
                color: 'var(--text-primary)',
                minWidth: '108px',
                height: '10px',
                transition: 'background 0.2s'
              }}
            >
              {t('tabs.iSearch')}
            </button>
          </div>

          {/* Search Bar - Issue #228: Search triggers only on icon click or Enter */}
          <div className="search-container" style={{ paddingTop: '0' }}>
            <div className="search-input-wrapper">
              <input
                type="text"
                className="search-input"
                placeholder={t('marketplace.searchPlaceholder') || 'Suche...'}
                value={marketplaceSearchQuery}
                onChange={(e) => setMarketplaceSearchQuery(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    setActiveMarketplaceSearch(marketplaceSearchQuery);
                  }
                }}
                aria-label={t('marketplace.searchAriaLabel') || 'Marktplatz durchsuchen'}
              />
              <button
                type="button"
                className="search-button"
                onClick={() => setActiveMarketplaceSearch(marketplaceSearchQuery)}
                aria-label={t('marketplace.searchButtonAriaLabel') || 'Suche starten'}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                  <path d="M21 21L16.65 16.65M19 11C19 15.4183 15.4183 19 11 19C6.58172 19 3 15.4183 3 11C3 6.58172 6.58172 3 11 3C15.4183 3 19 6.58172 19 11Z"
                    stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Issue #288: Search Bar for New Posts - triggers only on icon click or Enter */}
      {activeTab === 'new' && (
        <div className="search-container">
          <div className="search-input-wrapper">
            <input
              type="text"
              className="search-input"
              placeholder={t('posts.searchPlaceholder') || 'Beitrag suchen...'}
              value={postsSearchQuery}
              onChange={(e) => setPostsSearchQuery(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  setActivePostsSearch(postsSearchQuery);
                }
              }}
              aria-label={t('posts.searchAriaLabel') || 'Beiträge durchsuchen'}
            />
            <button
              type="button"
              className="search-button"
              onClick={() => setActivePostsSearch(postsSearchQuery)}
              aria-label={t('posts.searchButtonAriaLabel') || 'Suche starten'}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                <path d="M21 21L16.65 16.65M19 11C19 15.4183 15.4183 19 11 19C6.58172 19 3 15.4183 3 11C3 6.58172 6.58172 3 11 3C15.4183 3 19 6.58172 19 11Z"
                  stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>
          </div>
        </div>
      )}

      {/* Issue #61: "Nur Favoriten" Filter Button */}
      {activeTab === 'market' && (
        <div className="marketplace-favorites-filter">
          <button
            onClick={() => setMarketplaceFavoritesOnly(!marketplaceFavoritesOnly)}
            aria-pressed={marketplaceFavoritesOnly}
            aria-label={t('favorites.onlyFavorites') + (marketplaceFavoritesOnly ? ' - ' + (t('favorites.filterActive') || 'Filter aktiv') : '')}
            style={{
              display: 'flex',
              flexDirection: 'row',
              justifyContent: 'flex-start',
              alignItems: 'center',
              padding: marketplaceFavoritesOnly ? '4px 10px' : '0px',
              margin: '0px',
              gap: '5px',
              minWidth: '115px',
              height: '25px',
              background: marketplaceFavoritesOnly ? 'var(--bg-hover)' : 'transparent',
              border: 'none',
              borderRadius: marketplaceFavoritesOnly ? '12px' : '0',
              cursor: 'pointer',
              transition: 'all 0.2s ease'
            }}
            onMouseEnter={(e) => e.currentTarget.style.opacity = '0.7'}
            onMouseLeave={(e) => e.currentTarget.style.opacity = '1'}
          >
            {/* Heart Icon - Always filled since favorites are already hearted items */}
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="#FF0000"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M20.84 4.61C20.3292 4.099 19.7228 3.69365 19.0554 3.41708C18.3879 3.14052 17.6725 2.99817 16.95 2.99817C16.2275 2.99817 15.5121 3.14052 14.8446 3.41708C14.1772 3.69365 13.5708 4.099 13.06 4.61L12 5.67L10.94 4.61C9.9083 3.57831 8.50903 2.99871 7.05 2.99871C5.59096 2.99871 4.19169 3.57831 3.16 4.61C2.1283 5.64169 1.54871 7.04096 1.54871 8.5C1.54871 9.95903 2.1283 11.3583 3.16 12.39L4.22 13.45L12 21.23L19.78 13.45L20.84 12.39C21.351 11.8792 21.7563 11.2728 22.0329 10.6054C22.3095 9.93789 22.4518 9.22249 22.4518 8.5C22.4518 7.77751 22.3095 7.0621 22.0329 6.39464C21.7563 5.72718 21.351 5.12075 20.84 4.61Z"
                stroke="#FF0000"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>

            {/* "Nur Favoriten" Text - Issue #227: Bold brand color when active. Issue #375: use --brand-primary so dark mode swaps to a high-contrast tone. */}
            <span style={{
              fontFamily: 'Poppins, sans-serif',
              fontStyle: 'normal',
              fontWeight: marketplaceFavoritesOnly ? '700' : '400',
              fontSize: '13px',
              lineHeight: '20px',
              color: marketplaceFavoritesOnly ? 'var(--brand-primary)' : 'var(--text-primary)'
            }}>
              {t('favorites.onlyFavorites')}
            </span>
          </button>
        </div>
      )}

      {/* Posts Feed */}
      <main className="posts-feed">
        {postsLoading && (
          <p className="loading-text">{t('loadingPosts')}</p>
        )}
        {!postsLoading && posts.length === 0 && (
          <p className="no-posts-text">
            {activeTab === 'market' ? t('noResults') : t('posts.noPosts')}
          </p>
        )}
        {posts.map((post, index) => {
          const isReported = reportedPostIds.includes(post.id);
          // Issue #317: Check if this post is the highlighted shared post
          const isHighlighted = highlightedPostId === post.id;
          // Issue #311: Insert native ad every 5 posts (after indices 4, 9, 14, etc.)
          const showNativeAd = (index + 1) % 5 === 0 && index > 0;
          return (
          <React.Fragment key={post.id}>
          <article
            ref={isHighlighted ? sharedPostRef : null}
            className={`post-card ${isReported ? 'reported' : ''} ${isHighlighted ? 'shared-highlight' : ''}`}
            aria-label={isHighlighted ? (t('posts.sharedPost') || 'Geteilter Beitrag') : undefined}
          >
            {/* Issue #74: Show reported badge */}
            {isReported && <div className="reported-badge">{t('reported')}</div>}
            {/* Issue #58: Different display for marketplace objects */}
            {activeTab === 'market' ? (
              /* Marketplace Object Card */
              /* Issue #263: Added sold class and styling for sold objects */
              <div
                onClick={(isReported || post.sold_at) ? undefined : () => navigation.onNavigateToMarketplaceDetail(post)}
                className={`marketplace-card ${isReported ? 'reported' : ''} ${post.sold_at ? 'sold' : ''}`}
                style={{ cursor: (isReported || post.sold_at) ? 'not-allowed' : 'pointer' }}
              >
                {/* Issue #263: Sold badge for sold objects */}
                {/* Issue #329: Show GEFUNDEN for search objects */}
                {post.sold_at && (
                  <div className="sold-badge">
                    {post.marketplace_type === 'search' ? t('marketplace.found') : t('marketplace.sold')}
                  </div>
                )}
                {/* Row 1: Author Name and Time - left aligned */}
                <div style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '2px'
                }}>
                  {/* Issue #220: Show full author name (first and last name) */}
                  <span style={{
                    fontFamily: 'Poppins, sans-serif',
                    fontWeight: '700',
                    fontSize: '15px',
                    lineHeight: '135%',
                    color: 'var(--text-primary)'
                  }}>
                    {post.author_name}
                  </span>
                  <span style={{
                    fontFamily: 'Poppins, sans-serif',
                    fontWeight: '400',
                    fontSize: '10px',
                    lineHeight: '20px',
                    color: 'var(--text-secondary)'
                  }}>
                    {formatTimeAgo(post.created_time)}
                  </span>
                </div>

                {/* Issue #225: Preview image clickable to open gallery modal */}
                {post.images && post.images.length > 0 && (
                  <div
                    style={{
                      width: '80px',
                      height: '80px',
                      borderRadius: '8px',
                      overflow: 'hidden',
                      flexShrink: 0,
                      marginTop: '8px',
                      marginBottom: '8px'
                    }}
                  >
                    <img
                      src={`${API_BASE_URL}${post.images[0]}`}
                      alt={post.title || t('marketplace.previewImage') || 'Vorschaubild'}
                      role="button"
                      tabIndex={0}
                      aria-label={t('marketplace.openImageGallery') || 'Bild in Galerie öffnen'}
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedImageData({
                          images: [`${API_BASE_URL}${post.images[0]}`],
                          index: 0
                        });
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          e.stopPropagation();
                          setSelectedImageData({
                            images: [`${API_BASE_URL}${post.images[0]}`],
                            index: 0
                          });
                        }
                      }}
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        cursor: 'pointer'
                      }}
                    />
                  </div>
                )}

                {/* Row 2: Title and Price - left aligned */}
                <div style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '2px'
                }}>
                  <span style={{
                    fontFamily: 'Poppins, sans-serif',
                    fontWeight: '700',
                    fontSize: '15px',
                    lineHeight: '135%',
                    color: 'var(--text-primary)'
                  }}>
                    {post.title}
                  </span>
                  {/* Issue #83: Show "Zum Verschenken" for free giveaways, otherwise show price */}
                  {(post.price || post.is_free_giveaway) && (
                    <span style={{
                      fontFamily: 'Poppins, sans-serif',
                      fontWeight: '600',
                      fontSize: '13px',
                      lineHeight: '135%',
                      color: 'var(--text-primary)'
                    }}>
                      {post.is_free_giveaway ? t('marketplace.freeGiveaway') : `${post.price} €`}
                    </span>
                  )}
                </div>

                {/* Issue #60: Row 3: Hearts and Clicks counts - right-aligned like post actions */}
                <div className="post-footer">
                  <div className="post-actions">
                    {/* Issue #115/#385: Shared heart button (keyboard accessible, disabled when sold) */}
                    <MarketplaceHeartButton
                      className="marketplace-stat-item"
                      heartsCount={post.hearts_count}
                      userHasHearted={post.user_has_hearted}
                      isSold={!!post.sold_at}
                      onToggle={async () => {
                        try {
                          const response = await axios.post(`/api/marketplace/${post.id}/heart`);
                          // Update the post in the local state
                          setPosts(prevPosts => prevPosts.map(p =>
                            p.id === post.id
                              ? { ...p, hearts_count: response.data.hearts_count, user_has_hearted: response.data.user_has_hearted }
                              : p
                          ));
                        } catch (err) {
                          console.error('Error toggling heart:', err);
                        }
                      }}
                    />

                    {/* Clicks count */}
                    <div className="marketplace-stat-item">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M1 12C1 12 5 4 12 4C19 4 23 12 23 12C23 12 19 20 12 20C5 20 1 12 1 12Z" stroke="var(--brand-primary)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        <circle cx="12" cy="12" r="3" stroke="var(--brand-primary)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                      <span style={{
                        fontFamily: 'Poppins, sans-serif',
                        fontWeight: '400',
                        fontSize: '13px',
                        color: 'var(--text-secondary)'
                      }}>
                        {post.clicks_count || 0}
                      </span>
                    </div>

                    {/* Issue #62: 3-dot menu for marketplace objects */}
                    {/* Issue #115: Using shared MarketplaceObjectMenu component */}
                    {userData && (
                      <div className={`post-menu${openMenuPostId === post.id && menuOpenUpward ? ' post-menu-upward' : ''}`} onClick={(e) => e.stopPropagation()}>
                        <button
                          className="menu-button"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleMenuToggle(post.id, e);
                          }}
                          onKeyDown={(e) => {
                            if (e.key === 'Escape') {
                              setOpenMenuPostId(null);
                            } else if (e.key === 'ArrowDown' && openMenuPostId === post.id) {
                              e.preventDefault();
                              const menu = e.currentTarget.parentElement.querySelector('[role="menu"]');
                              const firstItem = menu?.querySelector('[role="menuitem"]');
                              firstItem?.focus();
                            }
                          }}
                          title="Optionen"
                          aria-haspopup="menu"
                          aria-expanded={openMenuPostId === post.id}
                          aria-label="Mehr Optionen"
                        >
                          ⋮
                        </button>
                        {/* Issue #263: Added onMarkAsSold and showMarkAsSold for owner's unsold objects */}
                        {/* Issue #312: Added onShare for sharing marketplace objects */}
                        {/* Issue #329: Added marketplaceType for correct button text */}
                        <MarketplaceObjectMenu
                          isOpen={openMenuPostId === post.id}
                          onClose={() => setOpenMenuPostId(null)}
                          onEdit={() => handleEditMarketplaceObject(post)}
                          onDelete={() => handleDeleteMarketplaceObject(post.id)}
                          onMarkAsSold={() => handleMarkAsSold(post.id)}
                          onShare={() => handleSharePost(post)}
                          onReport={() => handleReportPost(post.id)}
                          isOwner={post.user_id === userData.id}
                          showMarkAsSold={post.user_id === userData.id && !post.sold_at}
                          marketplaceType={post.marketplace_type}
                        />
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ) : (
              /* Regular Post Card */
              <>
                <div className="post-header">
                  <div className="post-author-info">
                    <span className="author-name">{post.author_name}</span>
                    <span className="post-time">
                      {formatTimeAgo(post.created_time)}
                      {/* Issue #32: Show "edited" mark for edited posts */}
                      {post.is_edited && <span className="edited-mark"> (bearbeitet)</span>}
                    </span>
                  </div>
                </div>

                {renderPostContent(post)}

                {/* Post Images - Only for regular posts */}
                {post.images && post.images.length > 0 && (
                  <div className="post-images" style={{ position: 'relative' }}>
                    <div className="photo-container">
                      <img
                        src={`${API_BASE_URL}${post.images[postImageIndices[post.id] || 0]}`}
                        alt={`Post - Foto ${(postImageIndices[post.id] || 0) + 1}`}
                        className="post-image"
                        onClick={() => setSelectedImageData({
                          images: post.images.map(img => `${API_BASE_URL}${img}`),
                          index: postImageIndices[post.id] || 0
                        })}
                        style={{ cursor: 'pointer' }}
                      />
                    </div>

                    {/* Navigation Buttons - Only show if there are multiple images and no popover is open */}
                    {/* Issue #96: Hide arrow buttons when edit post popover is open */}
                    {/* Issue #170: Also hide when delete confirmation modal is open */}
                    {post.images.length > 1 && !editingPost && !deletingPostId && (
                      <>
                        <button
                          className="gallery-nav-button gallery-nav-left"
                          onClick={(e) => {
                            e.stopPropagation();
                            handlePrevPostImage(post.id, post.images.length);
                          }}
                          aria-label="Vorheriges Bild"
                          style={{
                            position: 'absolute',
                            top: '50%',
                            left: '10px',
                            transform: 'translateY(-50%)',
                            zIndex: 9999,
                            pointerEvents: 'auto'
                          }}
                        >
                          &#8249;
                        </button>
                        <button
                          className="gallery-nav-button gallery-nav-right"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleNextPostImage(post.id, post.images.length);
                          }}
                          aria-label="Nächstes Bild"
                          style={{
                            position: 'absolute',
                            top: '50%',
                            right: '10px',
                            transform: 'translateY(-50%)',
                            zIndex: 9999,
                            pointerEvents: 'auto'
                          }}
                        >
                          &#8250;
                        </button>
                      </>
                    )}

                    {/* Photo Counter - Only show if there are multiple images */}
                    {post.images.length > 1 && (
                      <div className="photo-counter">
                        {(postImageIndices[post.id] || 0) + 1} / {post.images.length}
                      </div>
                    )}
                  </div>
                )}

                {/* Issue #58: Post actions and comments - only for regular posts */}
                <div className="post-footer">
                  <div className="post-actions">
                    <button
                      className={`action-button ${post.user_has_liked ? 'liked' : ''}`}
                      onClick={() => handleLikePost(post.id)}
                      title={post.user_has_liked ? "Like entfernen" : "Liken"}
                    >
                      <span className="icon">{post.user_has_liked ? '👍' : '👍'}</span>
                      <span className="count">{post.like_number}</span>
                    </button>
                    <button
                      className="action-button"
                      onClick={() => handleToggleComments(post.id)}
                      title="Kommentare anzeigen"
                    >
                      <span className="icon">💬</span>
                      <span className="count">{post.comment_number}</span>
                    </button>
                    {/* Issue #30, #32, #34: 3-dot menu */}
                    {/* Issue #115: Added keyboard accessibility */}
                    {userData && (
                      <div className={`post-menu${openMenuPostId === post.id && menuOpenUpward ? ' post-menu-upward' : ''}`}>
                        <button
                          className="menu-button"
                          onClick={(e) => handleMenuToggle(post.id, e)}
                          onKeyDown={(e) => {
                            if (e.key === 'Escape') {
                              setOpenMenuPostId(null);
                            } else if (e.key === 'ArrowDown' && openMenuPostId === post.id) {
                              e.preventDefault();
                              const menu = e.currentTarget.parentElement.querySelector('[role="menu"]');
                              const firstItem = menu?.querySelector('[role="menuitem"]');
                              firstItem?.focus();
                            }
                          }}
                          title="Optionen"
                          aria-haspopup="menu"
                          aria-expanded={openMenuPostId === post.id}
                          aria-label="Mehr Optionen"
                        >
                          ⋮
                        </button>
                        {openMenuPostId === post.id && (
                          <>
                            {/* Overlay for click-away functionality */}
                            <div
                              className="post-menu-overlay"
                              onClick={() => setOpenMenuPostId(null)}
                            ></div>
                            <div className="menu-dropdown" role="menu" aria-label="Optionen">
                              {/* Show Edit/Delete for own posts */}
                              {post.user_id === userData.id ? (
                                <>
                                  <button
                                    role="menuitem"
                                    tabIndex={0}
                                    onClick={() => handleEditPost(post)}
                                    onKeyDown={(e) => {
                                      if (e.key === 'Escape') {
                                        setOpenMenuPostId(null);
                                        e.currentTarget.closest('.post-menu').querySelector('.menu-button')?.focus();
                                      } else if (e.key === 'ArrowDown') {
                                        e.preventDefault();
                                        const next = e.currentTarget.nextElementSibling;
                                        if (next?.getAttribute('role') === 'menuitem') next.focus();
                                      } else if (e.key === 'ArrowUp') {
                                        e.preventDefault();
                                        const prev = e.currentTarget.previousElementSibling;
                                        if (prev?.getAttribute('role') === 'menuitem') prev.focus();
                                      }
                                    }}
                                  >
                                    {t('posts.editPost')}
                                  </button>
                                  {/* Issue #312: Share post button */}
                                  <button
                                    role="menuitem"
                                    tabIndex={0}
                                    onClick={() => handleSharePost(post)}
                                    onKeyDown={(e) => {
                                      if (e.key === 'Escape') {
                                        setOpenMenuPostId(null);
                                        e.currentTarget.closest('.post-menu').querySelector('.menu-button')?.focus();
                                      } else if (e.key === 'ArrowDown') {
                                        e.preventDefault();
                                        const next = e.currentTarget.nextElementSibling;
                                        if (next?.getAttribute('role') === 'menuitem') next.focus();
                                      } else if (e.key === 'ArrowUp') {
                                        e.preventDefault();
                                        const prev = e.currentTarget.previousElementSibling;
                                        if (prev?.getAttribute('role') === 'menuitem') prev.focus();
                                      }
                                    }}
                                  >
                                    {t('share.sharePost')}
                                  </button>
                                  <button
                                    role="menuitem"
                                    tabIndex={0}
                                    onClick={() => handleDeletePost(post.id)}
                                    onKeyDown={(e) => {
                                      if (e.key === 'Escape') {
                                        setOpenMenuPostId(null);
                                        e.currentTarget.closest('.post-menu').querySelector('.menu-button')?.focus();
                                      } else if (e.key === 'ArrowDown') {
                                        e.preventDefault();
                                        const next = e.currentTarget.nextElementSibling;
                                        if (next?.getAttribute('role') === 'menuitem') next.focus();
                                      } else if (e.key === 'ArrowUp') {
                                        e.preventDefault();
                                        const prev = e.currentTarget.previousElementSibling;
                                        if (prev?.getAttribute('role') === 'menuitem') prev.focus();
                                      }
                                    }}
                                    className="delete-option"
                                  >
                                    {t('posts.deletePost')}
                                  </button>
                                </>
                              ) : (
                                /* Show Share and Report for other users' posts */
                                <>
                                  {/* Issue #312: Share post button */}
                                  <button
                                    role="menuitem"
                                    tabIndex={0}
                                    onClick={() => handleSharePost(post)}
                                    onKeyDown={(e) => {
                                      if (e.key === 'Escape') {
                                        setOpenMenuPostId(null);
                                        e.currentTarget.closest('.post-menu').querySelector('.menu-button')?.focus();
                                      } else if (e.key === 'ArrowDown') {
                                        e.preventDefault();
                                        const next = e.currentTarget.nextElementSibling;
                                        if (next?.getAttribute('role') === 'menuitem') next.focus();
                                      } else if (e.key === 'ArrowUp') {
                                        e.preventDefault();
                                        const prev = e.currentTarget.previousElementSibling;
                                        if (prev?.getAttribute('role') === 'menuitem') prev.focus();
                                      }
                                    }}
                                  >
                                    {t('share.sharePost')}
                                  </button>
                                  <button
                                    role="menuitem"
                                    tabIndex={0}
                                    onClick={() => handleReportPost(post.id)}
                                    onKeyDown={(e) => {
                                      if (e.key === 'Escape') {
                                        setOpenMenuPostId(null);
                                        e.currentTarget.closest('.post-menu').querySelector('.menu-button')?.focus();
                                      } else if (e.key === 'ArrowDown') {
                                        e.preventDefault();
                                        const next = e.currentTarget.nextElementSibling;
                                        if (next?.getAttribute('role') === 'menuitem') next.focus();
                                      } else if (e.key === 'ArrowUp') {
                                        e.preventDefault();
                                        const prev = e.currentTarget.previousElementSibling;
                                        if (prev?.getAttribute('role') === 'menuitem') prev.focus();
                                      }
                                    }}
                                  >
                                    {t('posts.reportPost')}
                                  </button>
                                </>
                              )}
                            </div>
                          </>
                        )}
                      </div>
                    )}
                  </div>
                </div>

                {/* Issue #52: Comments section */}
                {openCommentsPostId === post.id && (
              <div className="comments-section" id={`comments-${post.id}`}>
                {/* Issue #67, #177: Using shared CommentInput component with anonymous toggle */}
                <CommentInput
                  postId={post.id}
                  value={commentText[post.id] || ''}
                  onChange={(text) => setCommentText(prev => ({ ...prev, [post.id]: text }))}
                  isAnonymous={commentIsAnonymous[post.id] || false}
                  onAnonymousChange={(checked) => setCommentIsAnonymous(prev => ({ ...prev, [post.id]: checked }))}
                  onSubmit={() => handleSubmitComment(post.id)}
                  isSubmitting={isSubmittingComment}
                />

                {/* Comments list */}
                <div className="comments-list">
                  {loadingComments ? (
                    <div className="comments-loading">{t('comments.loadingComments')}</div>
                  ) : comments[post.id] && comments[post.id].length > 0 ? (
                    comments[post.id]
                      .map((comment) => {
                        const isCommentReported = reportedCommentIds.includes(comment.id);
                        return (
                        <div key={comment.id} className={`comment-item ${isCommentReported ? 'reported' : ''}`}>
                          {/* Issue #413: badge overlay removed for reported comments — the
                              inline italic "reported comment" placeholder below now indicates
                              the state, keeping the metadata legible. */}
                          <div className="comment-header">
                            <div className="comment-author-info">
                              {/* Issue #141: Show "(Autor)" for anonymous comments from post author */}
                              <span className="comment-author">
                                {comment.is_anonymous && comment.is_post_author
                                  ? `${comment.author_name} (${t('comments.author')})`
                                  : comment.author_name.split(' ')[0]}
                              </span>
                              <span className="comment-time">{formatTimeAgo(comment.created_at)}</span>
                            </div>

                            {/* Issue #54, #57: Three-dot menu for comment edit/delete/report */}
                            {/* Issue #115: Added keyboard accessibility */}
                            <div className="comment-menu">
                              <button
                                className="comment-menu-button"
                                onClick={() => setOpenCommentMenuId(openCommentMenuId === comment.id ? null : comment.id)}
                                onKeyDown={(e) => {
                                  if (e.key === 'Escape') {
                                    setOpenCommentMenuId(null);
                                  } else if (e.key === 'ArrowDown' && openCommentMenuId === comment.id) {
                                    e.preventDefault();
                                    const menu = e.currentTarget.parentElement.querySelector('[role="menu"]');
                                    const firstItem = menu?.querySelector('[role="menuitem"]');
                                    firstItem?.focus();
                                  }
                                }}
                                aria-haspopup="menu"
                                aria-expanded={openCommentMenuId === comment.id}
                                aria-label="Kommentar-Optionen"
                              >
                                ⋮
                              </button>
                              {openCommentMenuId === comment.id && (
                                <>
                                  <div
                                    className="comment-menu-overlay"
                                    onClick={() => setOpenCommentMenuId(null)}
                                  ></div>
                                  <div className="comment-menu-dropdown" role="menu" aria-label="Kommentar-Optionen">
                                    {/* Issue #57: Show Edit/Delete for own comments */}
                                    {comment.user_id === userData.id ? (
                                      <>
                                        <button
                                          role="menuitem"
                                          tabIndex={0}
                                          onClick={() => handleEditComment(comment)}
                                          onKeyDown={(e) => {
                                            if (e.key === 'Escape') {
                                              setOpenCommentMenuId(null);
                                              e.currentTarget.closest('.comment-menu').querySelector('.comment-menu-button')?.focus();
                                            } else if (e.key === 'ArrowDown') {
                                              e.preventDefault();
                                              const next = e.currentTarget.nextElementSibling;
                                              if (next?.getAttribute('role') === 'menuitem') next.focus();
                                            } else if (e.key === 'ArrowUp') {
                                              e.preventDefault();
                                              const prev = e.currentTarget.previousElementSibling;
                                              if (prev?.getAttribute('role') === 'menuitem') prev.focus();
                                            }
                                          }}
                                        >
                                          {t('comments.editComment')}
                                        </button>
                                        <button
                                          role="menuitem"
                                          tabIndex={0}
                                          onClick={() => handleDeleteComment(comment.id, post.id)}
                                          onKeyDown={(e) => {
                                            if (e.key === 'Escape') {
                                              setOpenCommentMenuId(null);
                                              e.currentTarget.closest('.comment-menu').querySelector('.comment-menu-button')?.focus();
                                            } else if (e.key === 'ArrowDown') {
                                              e.preventDefault();
                                              const next = e.currentTarget.nextElementSibling;
                                              if (next?.getAttribute('role') === 'menuitem') next.focus();
                                            } else if (e.key === 'ArrowUp') {
                                              e.preventDefault();
                                              const prev = e.currentTarget.previousElementSibling;
                                              if (prev?.getAttribute('role') === 'menuitem') prev.focus();
                                            }
                                          }}
                                          className="delete-option"
                                        >
                                          {t('comments.deleteComment')}
                                        </button>
                                      </>
                                    ) : (
                                      /* Show Report for other users' comments */
                                      <button
                                        role="menuitem"
                                        tabIndex={0}
                                        onClick={() => handleReportComment(comment.id)}
                                        onKeyDown={(e) => {
                                          if (e.key === 'Escape') {
                                            setOpenCommentMenuId(null);
                                            e.currentTarget.closest('.comment-menu').querySelector('.comment-menu-button')?.focus();
                                          }
                                        }}
                                      >
                                        {t('comments.reportComment')}
                                      </button>
                                    )}
                                  </div>
                                </>
                              )}
                            </div>
                          </div>

                          {/* Issue #57: Edit mode for comments */}
                          {editingCommentId === comment.id ? (
                            <div className="comment-edit-container">
                              <textarea
                                className="comment-input"
                                value={editCommentText}
                                onChange={(e) => {
                                  setEditCommentText(e.target.value);
                                  e.target.style.height = 'auto';
                                  e.target.style.height = e.target.scrollHeight + 'px';
                                }}
                              />
                              <div className="comment-edit-buttons">
                                <button
                                  className="comment-edit-cancel"
                                  onClick={handleCancelEditComment}
                                  disabled={isSubmittingCommentEdit}
                                >
                                  {t('cancel')}
                                </button>
                                <button
                                  className="comment-edit-save"
                                  onClick={() => handleSaveEditComment(comment.id, post.id)}
                                  disabled={isSubmittingCommentEdit || !editCommentText.trim()}
                                >
                                  {isSubmittingCommentEdit ? t('posts.saving') : t('save')}
                                </button>
                              </div>
                            </div>
                          ) : isCommentReported ? (
                            /* Issue #413: replace reported comment body with italic placeholder */
                            <div className="comment-text reported-comment-text" role="note">
                              <i>{t('comments.reportedComment')}</i>
                            </div>
                          ) : (
                            <div className="comment-text">
                              {comment.comment_text}
                              {/* Issue #57: Show "edited" indicator */}
                              {comment.is_edited && (
                                <span className="comment-edited-indicator"> ({t('edit')})</span>
                              )}
                            </div>
                          )}
                        </div>
                      );})
                  ) : (
                    <div className="no-comments">{t('comments.noComments')}</div>
                  )}
                </div>
              </div>
                )}
              </>
            )}
          </article>
          {/* Issue #311: Native ad after every 5 posts */}
          {showNativeAd && <NativeAd slot="feed-native" index={index} />}
          </React.Fragment>
        );})}

          {/* Issue #311: Banner ad at end of feed */}
          {!postsLoading && posts.length > 0 && !hasMore && (
            <AdBanner slot="feed-end" />
          )}

          {/* Issue #190: Load more indicator */}
          {isLoadingMore && (
            <div className="posts-loading-more" role="status" aria-live="polite">{t('loadingMorePosts')}</div>
          )}

          {/* Issue #190: Sentinel element for infinite scroll */}
          {!postsLoading && hasMore && !isLoadingMore && posts.length > 0 && (
            <div ref={loadMoreRef} className="posts-load-more-sentinel" aria-hidden="true" />
          )}

          {/* Issue #190: End of list message */}
          {!postsLoading && !hasMore && posts.length > 0 && (
            <div className="posts-end-of-list" role="status">{t('allPostsLoaded')}</div>
          )}
      </main>
    </div>

      {/* Issue #230: Floating Plus Button for quick content creation */}
      <FloatingPlusButton
        onClick={() => {
          const navigateTo = activeTab === 'market'
            ? navigation.onNavigateToCreateMarketplace
            : navigation.onNavigateToCreatePost;
          navigateTo();
        }}
        ariaLabel={activeTab === 'market'
          ? (t('floatingPlusButton.createMarketplace') || 'Neues Marktplatz-Objekt erstellen')
          : (t('floatingPlusButton.createPost') || 'Neuen Beitrag erstellen')
        }
      />

      {/* Issue #56: Bottom Navigation Bar with Create Button - Using shared component */}
      <BottomNavigation
        activeTab="home"
        onCreateClick={() => setShowCreateOptions(!showCreateOptions)}
      />

      {/* Issue #56: Create Options Menu - Using shared component */}
      {/* Issue #101: Removed onCreatePost and onCreateMarketplace props to use NavigationContext */}
      <CreateOptionsMenu
        isOpen={showCreateOptions}
        onClose={() => setShowCreateOptions(false)}
      />

      {/* Issue #119: Image Modal with horizontal scroll navigation */}
      {selectedImageData && (
        <ImageModal
          images={selectedImageData.images}
          initialIndex={selectedImageData.index}
          onClose={() => {
            setSelectedImageData(null);
            // Remove hash when closing modal without scrolling to top
            if (window.location.hash === '#image-modal') {
              window.history.replaceState(null, '', window.location.pathname + window.location.search);
            }
          }}
        />
      )}

      {/* Issue #32: Edit Post Popover - Shared Component */}
      {editingPost && (
        <EditPostPopover
          post={editingPost}
          onClose={handleCancelEdit}
          onSave={handleSaveEdit}
        />
      )}

      {/* Issue #62 / #440: Edit Marketplace Object Modal - shared component */}
      {editingMarketplaceObject && (
        <EditMarketplaceObjectPopover
          object={editingMarketplaceObject}
          onClose={() => setEditingMarketplaceObject(null)}
          onSave={handleSaveMarketplaceEdit}
        />
      )}

      {/* Issue #34: Delete Confirmation Modal */}
      {/* Issue #115: Using shared ConfirmationModal for accessibility */}
      <ConfirmationModal
        isOpen={!!deletingPostId}
        onClose={handleCancelDelete}
        onConfirm={handleConfirmDelete}
        title={posts.find(p => p.id === deletingPostId)?.post_type === 'marketplace' ? t('marketplace.deleteObject') : t('posts.deletePost')}
        message={posts.find(p => p.id === deletingPostId)?.post_type === 'marketplace' ? t('marketplace.confirmDelete') : t('posts.confirmDelete')}
        confirmText={t('delete')}
        confirmingText={t('posts.deleting')}
        isConfirming={isDeletingPost}
        variant="danger"
      />

      {/* Issue #263: Mark as Sold Confirmation Modal for Marktplatz tab */}
      {/* Issue #329: Use correct text based on marketplace_type */}
      <ConfirmationModal
        isOpen={!!confirmSoldObjectId}
        onClose={handleCancelMarkSold}
        onConfirm={handleConfirmMarkSold}
        title={(() => {
          const obj = posts.find(p => p.id === confirmSoldObjectId);
          return obj?.marketplace_type === 'search' ? t('marketplace.markAsFound') : t('marketplace.markAsSold');
        })()}
        message={(() => {
          const obj = posts.find(p => p.id === confirmSoldObjectId);
          return obj?.marketplace_type === 'search' ? t('marketplace.markAsFoundConfirm') : t('marketplace.markAsSoldConfirm');
        })()}
        confirmText={t('yes')}
        confirmingText={t('marketplace.marking')}
        isConfirming={isMarkingSold}
        variant="default"
      />

      {/* Issue #30: Report post/marketplace modal - using shared ReportModal component */}
      <ReportModal
        isOpen={!!reportingPostId}
        onClose={handleCancelReport}
        onSubmit={handleSubmitReport}
        type={activeTab === 'market' ? 'marketplace' : 'post'}
        isSubmitting={isSubmittingReport}
      />

      {/* Issue #30: Report success message - using shared SuccessModal component */}
      <SuccessModal
        isOpen={showReportSuccess}
        onClose={() => setShowReportSuccess(false)}
        message={activeTab === 'market' ? t('report.objectReportedSuccess') : t('report.postReportedSuccess')}
      />

      {/* Issue #54: Report comment modal - using shared ReportModal component */}
      <ReportModal
        isOpen={!!reportingCommentId}
        onClose={handleCancelCommentReport}
        onSubmit={handleSubmitCommentReport}
        type="comment"
        isSubmitting={isSubmittingCommentReport}
      />

      {/* Issue #54: Comment report success message - using shared SuccessModal component */}
      <SuccessModal
        isOpen={showCommentReportSuccess}
        onClose={() => setShowCommentReportSuccess(false)}
        message={t('report.commentReportedSuccess')}
      />

      {/* Issue #57: Comment Delete Confirmation Modal - using shared ConfirmationModal component */}
      <ConfirmationModal
        isOpen={!!deletingCommentId}
        onClose={handleCancelDeleteComment}
        onConfirm={handleConfirmDeleteComment}
        title={t('comments.deleteComment')}
        message={t('comments.confirmDelete')}
        confirmText={t('delete')}
        confirmingText={t('comments.deleting')}
        isConfirming={isDeletingComment}
        variant="danger"
      />

      {/* Issue #161: Bad words modal for comments */}
      <BadWordsModal
        isOpen={showCommentBadWordsModal}
        onClose={() => setShowCommentBadWordsModal(false)}
        badWords={commentBadWords}
        contentType="comment"
      />

      {/* Issue #312: Share modal for posts and marketplace objects */}
      <ShareModal
        isOpen={shareModalOpen}
        onClose={() => setShareModalOpen(false)}
        shareUrl={`${window.location.origin}/share/${shareData.type}/${shareData.id}`}
        shareText={
          shareData.type === 'object'
            ? t('share.objectMessage').replace('{title}', shareData.content)
            : t('share.postMessage').replace('{content}', shareData.content)
        }
        shareTitle={shareData.title || 'UNIVERSE'}
        contentType={shareData.type}
      />

    </>
  );
};

export default LandingPage;
